import React, { useRef, useState, useEffect } from 'react';
import EditableText from './EditableText';
import { useEditMode } from '../contexts/EditModeContext';
import { GenericButton, GenericButtonEditor } from './GenericButton';

const PRESET_COLORS = [
  { name: 'Default', value: '' },
  { name: 'Charcoal', value: '#222222' },
  { name: 'Sleek Gray', value: '#666666' },
  { name: 'Elegant Gold', value: '#d4af37' },
  { name: 'Rose Gold', value: '#c88e8d' },
  { name: 'Sage Green', value: '#8a9a86' },
  { name: 'Navy Blue', value: '#1a2e40' },
  { name: 'Terracotta', value: '#c06c5c' },
  { name: 'Soft Cream', value: '#fdfbf7' },
  { name: 'Pure White', value: '#ffffff' }
];

const THEME_COLORS = [
  { name: 'Primary', value: 'var(--color-primary)', hex: '#6b8e6f' },
  { name: 'Accent', value: 'var(--color-accent)', hex: '#d4a574' },
  { name: 'Text', value: 'var(--color-text)', hex: '#3d3d3d' },
  { name: 'Text Light', value: 'var(--color-text-light)', hex: '#8a8a8a' }
];

function EditableTable({ table, onChange }) {
  const { isEditMode } = useEditMode();
  const tableId = useRef(Math.random().toString(36).substring(2, 9)).current;
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [focusedCell, setFocusedCell] = useState(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (activeColorPicker) {
        setActiveColorPicker(null);
      }
      // If clicking outside the table, clear the active focused cell
      if (!e.target.closest('.table-editable-premium')) {
        setFocusedCell(null);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [activeColorPicker]);

  const rows = table.rows || [];
  const columnWidths = table.columnWidths || Array(rows[0]?.cells?.length || 2).fill('auto');
  const rowHeights = table.rowHeights || Array(rows.length || 2).fill('auto');

  // Utility to update table state
  const updateTable = (updatedFields) => {
    onChange({
      ...table,
      ...updatedFields,
    });
  };

  // Cell updates
  const updateCell = (rIdx, cIdx, fields) => {
    const newRows = rows.map((row, ri) => {
      if (ri !== rIdx) return row;
      const newCells = row.cells.map((cell, ci) => {
        if (ci !== cIdx) return cell;
        return { ...cell, ...fields };
      });
      return { ...row, cells: newCells };
    });
    updateTable({ rows: newRows });
  };

  // Toggle cell type
  const toggleCellType = (rIdx, cIdx, type) => {
    if (type === 'image') {
      updateCell(rIdx, cIdx, { type: 'image', url: '', alt: '', value: undefined });
    } else if (type === 'text') {
      updateCell(rIdx, cIdx, { type: 'text', value: 'New cell text', url: undefined, alt: undefined });
    } else if (type === 'button') {
      updateCell(rIdx, cIdx, {
        type: 'button',
        text: 'Click Here',
        url: '#',
        icon: '',
        pack: 'default',
        color: 'var(--color-primary)',
        textColor: '#ffffff',
        styleType: 'solid',
        align: 'center',
        openInNewTab: false
      });
    }
  };

  // Reset Cell to Empty Configuration state
  const resetCell = (rIdx, cIdx) => {
    setFocusedCell(null);
    updateCell(rIdx, cIdx, {
      type: '',
      value: undefined,
      url: undefined,
      alt: undefined,
      text: undefined,
      icon: undefined,
      pack: undefined,
      color: undefined,
      textColor: undefined,
      styleType: undefined,
      align: undefined
    });
  };

  // Handle cell image upload
  const handleCellImageUpload = async (rIdx, cIdx, file) => {
    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await fetch('/api/upload-image', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      const imageUrl = data.url || data.image?.url;
      if (imageUrl) {
        updateCell(rIdx, cIdx, { url: imageUrl, alt: '' });
      }
    } catch (error) {
      console.error('Cell image upload failed:', error);
    }
  };

  // Drag-to-Resize Column handler
  const handleColMouseDown = (e, cIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const cellEl = e.target.closest('td') || e.target.closest('th');
    if (!cellEl) return;
    
    const startWidth = cellEl.getBoundingClientRect().width;
    const startX = e.clientX;

    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(50, startWidth + delta);
      const newWidths = [...columnWidths];
      newWidths[cIdx] = `${newWidth}px`;
      onChange({ ...table, columnWidths: newWidths });
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Drag-to-Resize Row handler
  const handleRowMouseDown = (e, rIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const rowEl = e.target.closest('tr');
    if (!rowEl) return;

    const startHeight = rowEl.getBoundingClientRect().height;
    const startY = e.clientY;

    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientY - startY;
      const newHeight = Math.max(30, startHeight + delta);
      const newHeights = [...rowHeights];
      newHeights[rIdx] = `${newHeight}px`;
      onChange({ ...table, rowHeights: newHeights });
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Drag-to-Resize Image handler
  const handleImgMouseDown = (e, rIdx, cIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const imgWrapperEl = e.target.closest('.cell-image-preview-wrapper-premium');
    const imgEl = imgWrapperEl.querySelector('img');
    const startWidth = imgEl.getBoundingClientRect().width;
    const startX = e.clientX;

    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(30, startWidth + delta);
      updateCell(rIdx, cIdx, { imageWidth: `${newWidth}px` });
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Adaptive Column Insertion
  const insertCol = (cIdx) => {
    setFocusedCell(null);
    const hasPercentage = columnWidths.some(w => typeof w === 'string' && w.includes('%'));
    let newColWidths;
    if (hasPercentage) {
      newColWidths = Array(columnWidths.length + 1).fill('auto');
    } else {
      newColWidths = [...columnWidths];
      newColWidths.splice(cIdx, 0, 'auto');
    }

    const newRows = rows.map((row) => {
      const newCells = [...row.cells];
      
      // Check if there is an active horizontal merge crossing this index
      let isCrossingMerge = false;
      for (let c = 0; c < cIdx; c++) {
        const cell = newCells[c];
        if (cell && cell.colSpan > 1 && c + cell.colSpan > cIdx) {
          // Yes, expand the colSpan of the merge root cell
          newCells[c] = { ...cell, colSpan: cell.colSpan + 1 };
          isCrossingMerge = true;
          break;
        }
      }

      if (isCrossingMerge) {
        // Insert a hidden cell to preserve merge grid layout
        newCells.splice(cIdx, 0, { type: '', colSpan: 1, rowSpan: 1, isHidden: true });
      } else {
        // Insert a visible, unconfigured cell
        newCells.splice(cIdx, 0, { type: '', colSpan: 1, rowSpan: 1, isHidden: false });
      }

      return { ...row, cells: newCells };
    });

    updateTable({ columnWidths: newColWidths, rows: newRows });
  };

  // Adaptive Row Insertion
  const insertRow = (rIdx) => {
    setFocusedCell(null);
    const newRowHeights = [...rowHeights];
    newRowHeights.splice(rIdx, 0, 'auto');

    const colCount = columnWidths.length;
    const newCells = [];

    for (let c = 0; c < colCount; c++) {
      // Check if there is an active vertical merge crossing this index
      let isCrossingMerge = false;
      for (let r = 0; r < rIdx; r++) {
        const cell = rows[r]?.cells[c];
        if (cell && cell.rowSpan > 1 && r + cell.rowSpan > rIdx) {
          isCrossingMerge = true;
          break;
        }
      }

      if (isCrossingMerge) {
        newCells.push({ type: '', colSpan: 1, rowSpan: 1, isHidden: true });
      } else {
        newCells.push({ type: '', colSpan: 1, rowSpan: 1, isHidden: false });
      }
    }

    // Now, if we crossed any vertical merge, we must increment the rowSpan of their root cells
    const updatedRows = rows.map((row, r) => {
      if (r >= rIdx) return row; // only check rows above insertion
      const updatedCells = row.cells.map((cell) => {
        if (cell && cell.rowSpan > 1 && r + cell.rowSpan > rIdx) {
          return { ...cell, rowSpan: cell.rowSpan + 1 };
        }
        return cell;
      });
      return { ...row, cells: updatedCells };
    });

    const newRow = { cells: newCells };
    const modifiedRows = [...updatedRows];
    modifiedRows.splice(rIdx, 0, newRow);

    updateTable({ rowHeights: newRowHeights, rows: modifiedRows });
  };

  // Helper to split any cells crossing a column (for column deletion)
  const splitCellsCrossingCol = (rowsList, targetColIdx) => {
    for (let r = 0; r < rowsList.length; r++) {
      for (let c = 0; c < rowsList[r].cells.length; c++) {
        const cell = rowsList[r].cells[c];
        if (cell && (cell.colSpan > 1 || cell.rowSpan > 1) && c <= targetColIdx && c + cell.colSpan - 1 >= targetColIdx) {
          const CS = cell.colSpan;
          const RS = cell.rowSpan;
          cell.colSpan = 1;
          cell.rowSpan = 1;
          for (let i = r; i < r + RS; i++) {
            for (let j = c; j < c + CS; j++) {
              if (i !== r || j !== c) {
                const sibling = rowsList[i].cells[j];
                if (sibling) {
                  sibling.isHidden = false;
                  sibling.colSpan = 1;
                  sibling.rowSpan = 1;
                  sibling.type = '';
                }
              }
            }
          }
        }
      }
    }
  };

  // Helper to split any cells crossing a row (for row deletion)
  const splitCellsCrossingRow = (rowsList, targetRowIdx) => {
    for (let r = 0; r < rowsList.length; r++) {
      for (let c = 0; c < rowsList[r].cells.length; c++) {
        const cell = rowsList[r].cells[c];
        if (cell && (cell.colSpan > 1 || cell.rowSpan > 1) && r <= targetRowIdx && r + cell.rowSpan - 1 >= targetRowIdx) {
          const CS = cell.colSpan;
          const RS = cell.rowSpan;
          cell.colSpan = 1;
          cell.rowSpan = 1;
          for (let i = r; i < r + RS; i++) {
            for (let j = c; j < c + CS; j++) {
              if (i !== r || j !== c) {
                const sibling = rowsList[i].cells[j];
                if (sibling) {
                  sibling.isHidden = false;
                  sibling.colSpan = 1;
                  sibling.rowSpan = 1;
                  sibling.type = '';
                }
              }
            }
          }
        }
      }
    }
  };

  // Delete Column
  const deleteCol = (cIdx) => {
    if (columnWidths.length <= 1) return;
    setFocusedCell(null);
    const newColWidths = columnWidths.filter((_, idx) => idx !== cIdx);
    
    const newRows = JSON.parse(JSON.stringify(rows));
    splitCellsCrossingCol(newRows, cIdx);

    const modifiedRows = newRows.map((row) => ({
      ...row,
      cells: row.cells.filter((_, idx) => idx !== cIdx),
    }));

    updateTable({ columnWidths: newColWidths, rows: modifiedRows });
  };

  // Delete Row
  const deleteRow = (rIdx) => {
    if (rows.length <= 1) return;
    setFocusedCell(null);
    const newRowHeights = rowHeights.filter((_, idx) => idx !== rIdx);

    const newRows = JSON.parse(JSON.stringify(rows));
    splitCellsCrossingRow(newRows, rIdx);

    const modifiedRows = newRows.filter((_, idx) => idx !== rIdx);

    updateTable({ rowHeights: newRowHeights, rows: modifiedRows });
  };

  // Merge Validators
  const canMergeRight = (rIdx, cIdx) => {
    const cell = rows[rIdx]?.cells[cIdx];
    if (!cell) return false;
    const CS = cell.colSpan || 1;
    const RS = cell.rowSpan || 1;
    const targetCol = cIdx + CS;

    if (targetCol >= columnWidths.length) return false;

    // Verify all target cells inside height bounds are unmerged and not hidden
    for (let i = rIdx; i < rIdx + RS; i++) {
      const targetCell = rows[i]?.cells[targetCol];
      if (!targetCell) return false;
      if (targetCell.colSpan > 1 || targetCell.rowSpan > 1 || targetCell.isHidden) {
        return false;
      }
    }
    return true;
  };

  const mergeRight = (rIdx, cIdx) => {
    if (!canMergeRight(rIdx, cIdx)) return;
    const newRows = JSON.parse(JSON.stringify(rows));
    const cell = newRows[rIdx].cells[cIdx];
    const CS = cell.colSpan || 1;
    const RS = cell.rowSpan || 1;
    const targetCol = cIdx + CS;
    const targetCell = newRows[rIdx].cells[targetCol];

    const targetCS = targetCell.colSpan || 1;
    cell.colSpan = CS + targetCS;

    for (let i = rIdx; i < rIdx + RS; i++) {
      for (let j = targetCol; j < targetCol + targetCS; j++) {
        newRows[i].cells[j].isHidden = true;
      }
    }

    if (cell.type === 'text' && targetCell.type === 'text' && targetCell.value) {
      cell.value = (cell.value || '') + ' ' + targetCell.value;
    }

    updateTable({ rows: newRows });
  };

  const canMergeDown = (rIdx, cIdx) => {
    const cell = rows[rIdx]?.cells[cIdx];
    if (!cell) return false;
    const CS = cell.colSpan || 1;
    const RS = cell.rowSpan || 1;
    const targetRow = rIdx + RS;

    if (targetRow >= rows.length) return false;

    // Verify all target cells inside width bounds are unmerged and not hidden
    for (let j = cIdx; j < cIdx + CS; j++) {
      const targetCell = rows[targetRow]?.cells[j];
      if (!targetCell) return false;
      if (targetCell.colSpan > 1 || targetCell.rowSpan > 1 || targetCell.isHidden) {
        return false;
      }
    }
    return true;
  };

  const mergeDown = (rIdx, cIdx) => {
    if (!canMergeDown(rIdx, cIdx)) return;
    const newRows = JSON.parse(JSON.stringify(rows));
    const cell = newRows[rIdx].cells[cIdx];
    const CS = cell.colSpan || 1;
    const RS = cell.rowSpan || 1;
    const targetRow = rIdx + RS;
    const targetCell = newRows[targetRow].cells[cIdx];

    const targetRS = targetCell.rowSpan || 1;
    cell.rowSpan = RS + targetRS;

    for (let i = targetRow; i < targetRow + targetRS; i++) {
      for (let j = cIdx; j < cIdx + CS; j++) {
        newRows[i].cells[j].isHidden = true;
      }
    }

    if (cell.type === 'text' && targetCell.type === 'text' && targetCell.value) {
      cell.value = (cell.value || '') + ' ' + targetCell.value;
    }

    updateTable({ rows: newRows });
  };

  const splitCell = (rIdx, cIdx) => {
    const newRows = JSON.parse(JSON.stringify(rows));
    const cell = newRows[rIdx].cells[cIdx];
    const CS = cell.colSpan || 1;
    const RS = cell.rowSpan || 1;

    cell.colSpan = 1;
    cell.rowSpan = 1;

    for (let i = rIdx; i < rIdx + RS; i++) {
      for (let j = cIdx; j < cIdx + CS; j++) {
        if (i !== rIdx || j !== cIdx) {
          const sibling = newRows[i].cells[j];
          if (sibling) {
            sibling.isHidden = false;
            sibling.colSpan = 1;
            sibling.rowSpan = 1;
            sibling.type = '';
            sibling.value = '';
          }
        }
      }
    }

    updateTable({ rows: newRows });
  };

  const cycleAlign = (rIdx, cIdx) => {
    const cell = rows[rIdx]?.cells[cIdx];
    if (!cell) return;
    const current = cell.align || 'left';
    const next = current === 'left' ? 'center' : current === 'center' ? 'right' : 'left';
    updateCell(rIdx, cIdx, { align: next });
  };

  const cycleValign = (rIdx, cIdx) => {
    const cell = rows[rIdx]?.cells[cIdx];
    if (!cell) return;
    const current = cell.valign || 'top';
    const next = current === 'top' ? 'middle' : current === 'middle' ? 'bottom' : 'top';
    updateCell(rIdx, cIdx, { valign: next });
  };



  const cycleTextTransform = (rIdx, cIdx) => {
    const cell = rows[rIdx]?.cells[cIdx];
    if (!cell) return;
    const current = cell.textTransform || 'none';
    const next = current === 'none' ? 'uppercase' : current === 'uppercase' ? 'capitalize' : current === 'capitalize' ? 'lowercase' : 'none';
    updateCell(rIdx, cIdx, { textTransform: next });
  };

  if (!isEditMode) {
    // Normal published view mode
    return (
      <div className="table-responsive">
        <table className="layout-table">
          <colgroup>
            {columnWidths.map((width, idx) => (
              <col 
                key={idx} 
                style={width === 'auto' ? {} : { 
                  width: `var(--col-width)`, 
                  '--col-width': width 
                }} 
              />
            ))}
          </colgroup>
          <tbody>
            {rows.map((row, rIdx) => (
              <tr 
                key={rIdx} 
                style={rowHeights[rIdx] === 'auto' ? {} : { 
                  height: `var(--row-height)`, 
                  '--row-height': rowHeights[rIdx] 
                }}
              >
                {row.cells.map((cell, cIdx) => {
                  if (cell.isHidden) return null;
                  return (
                    <td
                      key={cIdx}
                      colSpan={cell.colSpan || 1}
                      rowSpan={cell.rowSpan || 1}
                      className="layout-table-cell"
                      style={{
                        textAlign: cell.align || 'left',
                        verticalAlign: cell.valign || 'top',
                        color: cell.color || 'inherit',
                        fontWeight: cell.bold ? 'bold' : 'inherit',
                        fontStyle: cell.italic ? 'italic' : 'inherit',
                        fontFamily: cell.fontFamily || 'inherit',
                        textTransform: cell.textTransform || 'none',
                        fontSize: `var(--cell-font-size)`,
                        '--cell-font-size': cell.fontSize || '1.1rem'
                      }}
                    >
                      {cell.type === 'image' ? (
                        cell.url ? (
                          <img
                            src={cell.url}
                            alt={cell.alt || ''}
                            style={{
                              width: cell.imageWidth || '100%',
                              height: 'auto',
                              display: 'block',
                              borderRadius: '4px',
                              marginLeft: cell.align === 'center' ? 'auto' : cell.align === 'right' ? 'auto' : '0',
                              marginRight: cell.align === 'center' ? 'auto' : cell.align === 'left' ? 'auto' : '0'
                            }}
                            loading="lazy"
                          />
                        ) : null
                      ) : cell.type === 'text' ? (
                        <div 
                          style={{ whiteSpace: 'pre-wrap' }} 
                          dangerouslySetInnerHTML={{ __html: cell.value || '' }} 
                        />
                      ) : cell.type === 'button' ? (
                        <GenericButton
                          item={cell}
                          isEditMode={false}
                        />
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Edit Mode (Notion/Jira interactive style)
  return (
    <div className="table-editor-container premium-editor">
      <div className="table-responsive">
        <table className={`layout-table table-editable-premium ${focusedCell ? 'has-focused-cell' : ''}`}>
          <colgroup>
            {columnWidths.map((width, idx) => (
              <col 
                key={idx} 
                style={width === 'auto' ? {} : { 
                  width: `var(--col-width)`, 
                  '--col-width': width 
                }} 
              />
            ))}
          </colgroup>
          <tbody>
            {rows.map((row, rIdx) => (
              <tr 
                key={rIdx} 
                style={rowHeights[rIdx] === 'auto' ? {} : { 
                  height: `var(--row-height)`, 
                  '--row-height': rowHeights[rIdx] 
                }}
              >
                {row.cells.map((cell, cIdx) => {
                  if (cell.isHidden) return null;
                  
                  const isFocused = focusedCell && focusedCell.rIdx === rIdx && focusedCell.cIdx === cIdx;
                  return (
                    <td
                      key={cIdx}
                      colSpan={cell.colSpan || 1}
                      rowSpan={cell.rowSpan || 1}
                      className={`layout-table-cell premium-editable-cell ${cell.type ? `configured-${cell.type}` : 'unconfigured-cell'} ${isFocused ? 'focused' : ''}`}
                      onClick={() => setFocusedCell({ rIdx, cIdx })}
                      style={{
                        textAlign: cell.align || 'left',
                        verticalAlign: cell.valign || 'top',
                        color: cell.color || 'inherit',
                        fontWeight: cell.bold ? 'bold' : 'inherit',
                        fontStyle: cell.italic ? 'italic' : 'inherit',
                        fontFamily: cell.fontFamily || 'inherit',
                        textTransform: cell.textTransform || 'none',
                        fontSize: `var(--cell-font-size)`,
                        '--cell-font-size': cell.fontSize || '1.1rem'
                      }}
                    >
                      {/* 1. Integrated Choice Cards inside Cell if Unconfigured */}
                      {!cell.type ? (
                        <div className="cell-setup-cards">
                          <button
                            type="button"
                            className="cell-setup-card card-text"
                            onClick={() => toggleCellType(rIdx, cIdx, 'text')}
                            title="Format cell as Text"
                          >
                            <span className="card-icon">📝</span>
                            <span className="card-label">Text</span>
                          </button>
                          <button
                            type="button"
                            className="cell-setup-card card-image"
                            onClick={() => toggleCellType(rIdx, cIdx, 'image')}
                            title="Format cell as Image"
                          >
                            <span className="card-icon">📷</span>
                            <span className="card-label">Image</span>
                          </button>
                          <button
                            type="button"
                            className="cell-setup-card card-button"
                            onClick={() => toggleCellType(rIdx, cIdx, 'button')}
                            title="Format cell as Button"
                          >
                            <span className="card-icon">🔘</span>
                            <span className="card-label">Button</span>
                          </button>
                        </div>
                      ) : (
                        // 2. Active Cell Editors
                        <div className="cell-editor-body">
                          {/* Hover action menu with sleek icons */}
                          <div className="cell-actions-menu-floating">
                            {/* Font Family Selector Dropdown */}
                            <select
                              value={cell.fontFamily || ''}
                              onChange={(e) => updateCell(rIdx, cIdx, { fontFamily: e.target.value })}
                              className="cell-action-menu-select font-family-select"
                              title="Set Font Family"
                              style={{ fontFamily: cell.fontFamily || 'inherit' }}
                            >
                              <option value="" style={{ fontFamily: 'inherit' }}>Font: Default</option>
                              <option value="Gotham" style={{ fontFamily: 'Gotham' }}>Gotham</option>
                              <option value="Halimun" style={{ fontFamily: 'Halimun' }}>Halimun</option>
                              <option value="RegularText" style={{ fontFamily: 'RegularText' }}>Regular Text</option>
                              <option value="Cursive" style={{ fontFamily: 'Cursive' }}>Cursive</option>
                              <option value="Cursive2" style={{ fontFamily: 'Cursive2' }}>Cursive Elegant</option>
                              <option value="HeroNames" style={{ fontFamily: 'HeroNames' }}>Hero Names</option>
                              <option value="Date" style={{ fontFamily: 'Date' }}>Date/Display</option>
                              <option value="serif" style={{ fontFamily: 'serif' }}>Serif</option>
                              <option value="sans-serif" style={{ fontFamily: 'sans-serif' }}>Sans-Serif</option>
                              <option value="monospace" style={{ fontFamily: 'monospace' }}>Monospace</option>
                            </select>

                            <button
                              onClick={() => cycleAlign(rIdx, cIdx)}
                              className="cell-action-menu-btn"
                              title={`Align: ${cell.align === 'center' ? 'Center' : cell.align === 'right' ? 'Right' : 'Left'}`}
                              type="button"
                            >
                              {cell.align === 'center' ? '⇹ Center' : cell.align === 'right' ? '⇥ Right' : '⇤ Left'}
                            </button>
                            <button
                              onClick={() => cycleValign(rIdx, cIdx)}
                              className="cell-action-menu-btn"
                              title={`Vertical: ${cell.valign === 'middle' ? 'Middle' : cell.valign === 'bottom' ? 'Bottom' : 'Top'}`}
                              type="button"
                            >
                              {cell.valign === 'middle' ? '⇳ Middle' : cell.valign === 'bottom' ? '⤓ Bottom' : '⤒ Top'}
                            </button>

                            {/* Font Size Selector Dropdown */}
                            <select
                              value={cell.fontSize || ''}
                              onChange={(e) => updateCell(rIdx, cIdx, { fontSize: e.target.value })}
                              className="cell-action-menu-select font-size-select"
                              title="Set Font Size"
                            >
                              <option value="">Size: Default</option>
                              <option value="6pt">6 pt</option>
                              <option value="8pt">8 pt</option>
                              <option value="9pt">9 pt</option>
                              <option value="10pt">10 pt</option>
                              <option value="11pt">11 pt</option>
                              <option value="12pt">12 pt</option>
                              <option value="14pt">14 pt</option>
                              <option value="16pt">16 pt</option>
                              <option value="18pt">18 pt</option>
                              <option value="20pt">20 pt</option>
                              <option value="24pt">24 pt</option>
                              <option value="30pt">30 pt</option>
                              <option value="36pt">36 pt</option>
                              <option value="48pt">48 pt</option>
                            </select>

                            <button
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent button from stealing focus
                                const sel = window.getSelection();
                                if (sel && sel.rangeCount > 0 && sel.toString().trim().length > 0) {
                                  document.execCommand('bold', false, null);
                                } else {
                                  // Fallback: toggle entire cell bold
                                  updateCell(rIdx, cIdx, { bold: !cell.bold });
                                }
                              }}
                              className={`cell-action-menu-btn bold-btn ${cell.bold ? 'active' : ''}`}
                              title="Bold Selection (or toggle cell bold)"
                              type="button"
                              style={{ fontWeight: 'bold' }}
                            >
                              B
                            </button>

                            <button
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent button from stealing focus
                                const sel = window.getSelection();
                                if (sel && sel.rangeCount > 0 && sel.toString().trim().length > 0) {
                                  document.execCommand('italic', false, null);
                                } else {
                                  // Fallback: toggle entire cell italic
                                  updateCell(rIdx, cIdx, { italic: !cell.italic });
                                }
                              }}
                              className={`cell-action-menu-btn italic-btn ${cell.italic ? 'active' : ''}`}
                              title="Italic Selection (or toggle cell italic)"
                              type="button"
                              style={{ fontStyle: 'italic' }}
                            >
                              I
                            </button>

                            <button
                              onClick={() => cycleTextTransform(rIdx, cIdx)}
                              className={`cell-action-menu-btn case-btn ${cell.textTransform && cell.textTransform !== 'none' ? 'active' : ''}`}
                              title={`Text Case: ${
                                cell.textTransform === 'uppercase' ? 'UPPERCASE' :
                                cell.textTransform === 'capitalize' ? 'Capitalize' :
                                cell.textTransform === 'lowercase' ? 'lowercase' :
                                'Normal'
                              }`}
                              type="button"
                            >
                              🔠 {
                                cell.textTransform === 'uppercase' ? 'AA' :
                                cell.textTransform === 'capitalize' ? 'Aa' :
                                cell.textTransform === 'lowercase' ? 'aa' :
                                'Case'
                              }
                            </button>

                            <div className="cell-action-menu-btn color-picker-btn" title="Set Font Color">
                              <button
                                type="button"
                                className="color-picker-toggle-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveColorPicker(activeColorPicker === `${rIdx}-${cIdx}` ? null : `${rIdx}-${cIdx}`);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '0.7rem',
                                  color: 'inherit'
                                }}
                              >
                                🎨
                                <div
                                  className="color-picker-swatch"
                                  style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '2px',
                                    background: cell.color || '#333333',
                                    border: '1px solid rgba(0,0,0,0.15)'
                                  }}
                                />
                              </button>

                              {activeColorPicker === `${rIdx}-${cIdx}` && (
                                <div className="cell-color-popover" onClick={(e) => e.stopPropagation()}>
                                  <div className="color-popover-header">
                                    <span className="color-popover-title">Text Color</span>
                                    <button
                                      type="button"
                                      className="color-popover-close-btn"
                                      onClick={() => setActiveColorPicker(null)}
                                    >
                                      ×
                                    </button>
                                  </div>
                                  
                                  <div className="color-section-label">Theme Colors</div>
                                  <div className="color-presets-grid theme-colors-grid">
                                    {THEME_COLORS.map((col) => (
                                      <button
                                        key={col.name}
                                        type="button"
                                        className={`color-preset-dot ${cell.color === col.value ? 'active' : ''}`}
                                        style={{ background: col.hex }}
                                        onClick={() => {
                                          updateCell(rIdx, cIdx, { color: col.value });
                                          setActiveColorPicker(null);
                                        }}
                                        title={`${col.name} (${col.value})`}
                                      />
                                    ))}
                                  </div>

                                  <div className="color-section-label">Presets</div>
                                  <div className="color-presets-grid">
                                    {PRESET_COLORS.map((col) => (
                                      <button
                                        key={col.name}
                                        type="button"
                                        className={`color-preset-dot ${(!cell.color && col.value === '') || cell.color === col.value ? 'active' : ''}`}
                                        style={{ background: col.value || '#eeeeee' }}
                                        onClick={() => {
                                          updateCell(rIdx, cIdx, { color: col.value || undefined });
                                          setActiveColorPicker(null);
                                        }}
                                        title={col.name}
                                      >
                                        {col.value === '' && <span className="default-slash">/</span>}
                                      </button>
                                    ))}
                                  </div>
                                  
                                  <div className="color-custom-row">
                                    <span className="custom-label">Custom:</span>
                                    <div className="custom-color-input-wrapper">
                                      <input
                                        type="color"
                                        value={
                                          cell.color && cell.color.startsWith('#') && cell.color.length === 7
                                            ? cell.color
                                            : cell.color && cell.color.startsWith('var(')
                                            ? THEME_COLORS.find(tc => tc.value === cell.color)?.hex || '#333333'
                                            : '#333333'
                                        }
                                        onChange={(e) => updateCell(rIdx, cIdx, { color: e.target.value })}
                                        className="custom-color-input-field"
                                      />
                                      <input
                                        type="text"
                                        value={cell.color || ''}
                                        placeholder="Inherit"
                                        onChange={(e) => {
                                          let val = e.target.value;
                                          updateCell(rIdx, cIdx, { color: val || undefined });
                                        }}
                                        className="custom-color-hex-input"
                                        style={{
                                          width: '75px',
                                          fontSize: '0.65rem',
                                          padding: '2px 4px',
                                          border: '1px solid rgba(0,0,0,0.12)',
                                          borderRadius: '3px',
                                          fontFamily: 'monospace',
                                          textAlign: 'left'
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {canMergeRight(rIdx, cIdx) && (
                              <button
                                onClick={() => mergeRight(rIdx, cIdx)}
                                className="cell-action-menu-btn"
                                title="Merge cell right"
                              >
                                ➔
                              </button>
                            )}
                            {canMergeDown(rIdx, cIdx) && (
                              <button
                                onClick={() => mergeDown(rIdx, cIdx)}
                                className="cell-action-menu-btn"
                                title="Merge cell down"
                              >
                                ▼
                              </button>
                            )}
                            {(cell.colSpan > 1 || cell.rowSpan > 1) && (
                              <button
                                onClick={() => splitCell(rIdx, cIdx)}
                                className="cell-action-menu-btn split"
                                title="Split merged cell"
                              >
                                ↱↲
                              </button>
                            )}
                            
                            {/* Inline Delete row/col actions inside the cell bar */}
                            {columnWidths.length > 1 && (
                              <button
                                onClick={() => deleteCol(cIdx)}
                                className="cell-action-menu-btn delete-col-btn"
                                title={`Delete Column ${cIdx + 1}`}
                              >
                                🗑️ Col
                              </button>
                            )}
                            {rows.length > 1 && (
                              <button
                                onClick={() => deleteRow(rIdx)}
                                className="cell-action-menu-btn delete-row-btn"
                                title={`Delete Row ${rIdx + 1}`}
                              >
                                🗑️ Row
                              </button>
                            )}

                            {/* Reset cell button */}
                            <button
                              onClick={() => resetCell(rIdx, cIdx)}
                              className="cell-action-menu-btn reset"
                              title="Reset cell back to empty card layout"
                            >
                              🔄 Clear
                            </button>

                            {/* Help tooltip */}
                            <div 
                              className="cell-action-menu-btn help-btn" 
                              title="HTML formatting is supported! Use tags like <b>bold</b>, <i>italic</i>, or <span style='font-family: Cursive'>cursive</span> inside the text to format parts of the cell."
                              style={{ cursor: 'help', userSelect: 'none' }}
                            >
                              ℹ️
                            </div>
                          </div>

                          {cell.type === 'image' ? (
                            <div
                              className="cell-image-editor-container"
                              style={{
                                alignItems: cell.align === 'center' ? 'center' : cell.align === 'right' ? 'flex-end' : 'flex-start'
                              }}
                            >
                              {cell.url ? (
                                <div className="cell-image-preview-wrapper-premium" style={{ width: cell.imageWidth || '100%' }}>
                                  <img
                                    src={cell.url}
                                    alt={cell.alt || ''}
                                    className="cell-image-preview-premium"
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      display: 'block',
                                      marginLeft: cell.align === 'center' ? 'auto' : cell.align === 'right' ? 'auto' : '0',
                                      marginRight: cell.align === 'center' ? 'auto' : cell.align === 'left' ? 'auto' : '0'
                                    }}
                                  />
                                  <button
                                    onClick={() => updateCell(rIdx, cIdx, { url: '', alt: '' })}
                                    className="cell-image-remove-btn-premium"
                                    title="Remove uploaded image"
                                    type="button"
                                  >
                                    ×
                                  </button>
                                  <div
                                    className="image-resizer-handle-corner"
                                    onMouseDown={(e) => handleImgMouseDown(e, rIdx, cIdx)}
                                    title="Drag corner horizontally to scale image width"
                                  />
                                </div>
                              ) : (
                                <div className="cell-image-upload-wrapper-premium">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    id={`file-${tableId}-${rIdx}-${cIdx}`}
                                    className="cell-image-file-input-premium"
                                    onChange={(e) =>
                                      e.target.files[0] &&
                                      handleCellImageUpload(rIdx, cIdx, e.target.files[0])
                                    }
                                  />
                                  <label
                                    htmlFor={`file-${tableId}-${rIdx}-${cIdx}`}
                                    className="cell-image-upload-label-premium"
                                  >
                                    <span className="upload-icon">📁</span>
                                    <span className="upload-text">Upload Image</span>
                                  </label>
                                </div>
                              )}
                              {cell.url && (
                                <EditableText
                                  value={cell.alt || ''}
                                  onChange={(newAlt) => updateCell(rIdx, cIdx, { alt: newAlt })}
                                  className="cell-image-alt-editor-premium"
                                  tag="div"
                                  placeholder="Alternative text description"
                                />
                              )}
                            </div>
                          ) : cell.type === 'button' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                              <GenericButton
                                item={cell}
                                isEditMode={true}
                                onClick={(e) => e.preventDefault()}
                              />
                              {isFocused && (
                                <div 
                                  style={{ 
                                    marginTop: '0.5rem', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '6px', 
                                    background: 'white',
                                    padding: '1rem', 
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    width: 'max-content',
                                    minWidth: '320px',
                                    maxWidth: '90vw',
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 1000
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <GenericButtonEditor
                                    item={cell}
                                    onChange={(updatedFields) => updateCell(rIdx, cIdx, updatedFields)}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <EditableText
                              value={cell.value || ''}
                              onChange={(newVal) => updateCell(rIdx, cIdx, { value: newVal })}
                              className="cell-text-editor-premium"
                              tag="div"
                              html={false}
                            />
                          )}
                        </div>
                      )}

                      {/* 3. Drag-to-Resize Guidelines (Handles) */}
                      {/* Vertical Col Resizer Handle on right margin */}
                      <div
                        className="col-resizer-handle"
                        onMouseDown={(e) => handleColMouseDown(e, cIdx)}
                        title="Drag horizontally to resize column width"
                      />
                      {/* Horizontal Row Resizer Handle on bottom margin */}
                      <div
                        className="row-resizer-handle"
                        onMouseDown={(e) => handleRowMouseDown(e, rIdx)}
                        title="Drag vertically to resize row height"
                      />

                      {/* 4. Margin Hover insertion guides (+ buttons) */}
                      {/* Vertical Column Inserter (hover on right border) */}
                      <div
                        className="col-inserter-bar"
                        title={`Insert Column after Col ${cIdx + 1}`}
                      >
                        <div className="col-inserter-line" />
                        <div className="col-inserter-btn" onClick={(e) => { e.stopPropagation(); insertCol(cIdx + 1); }}>＋</div>
                      </div>

                      {/* Horizontal Row Inserter (hover on bottom border) */}
                      <div
                        className="row-inserter-bar"
                        title={`Insert Row after Row ${rIdx + 1}`}
                      >
                        <div className="row-inserter-line" />
                        <div className="row-inserter-btn" onClick={(e) => { e.stopPropagation(); insertRow(rIdx + 1); }}>＋</div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EditableTable;
